/**
 * Date format strings (dayjs tokens) shared by the client and, later, the server: the formats typed
 * dates are read in, and the named display formats (presets).
 *
 * This module works on format strings only and must not import dayjs: the client and the server each
 * have their own dayjs, and a second copy in the client bundle would have no locale or plugins set.
 * Callers pass in the locale's short date pattern, e.g. `dayjs().localeData().longDateFormat('L')`
 * (`MM/DD/YYYY` for en, `DD/MM/YYYY` for en-NZ (also Australia), `YYYY-MM-DD` for sv).
 */

/**
 * The same format with single-digit days and months allowed (`DD/MM/YYYY` → `D/M/YYYY`), for reading
 * typed dates. Month names (`MMM`, `MMMM`) and day names are left alone.
 */
export function looseDateFormat(format: string): string {
    return format.replace(/(?<!D)DD(?!D)/g, 'D').replace(/(?<!M)MM(?!M)/g, 'M');
}

/**
 * The formats a typed date may be in, for a strict parse: the format with each of the day and month
 * written with or without a leading zero (`DD/MM/YYYY`, `D/MM/YYYY`, `DD/M/YYYY`, `D/M/YYYY`). A strict
 * parse only accepts text the format reproduces exactly, so every mix is listed. A strict parse with
 * these still rejects text in another order (a US-style `08/22/2026` against `DD/MM/YYYY`) instead of
 * rolling it over to another date.
 */
export function typedDateFormats(format: string): string[] {
    const looseDay = (f: string): string => f.replace(/(?<!D)DD(?!D)/g, 'D');
    const looseMonth = (f: string): string => f.replace(/(?<!M)MM(?!M)/g, 'M');
    return [...new Set([format, looseDay(format), looseMonth(format), looseDateFormat(format)])];
}

/**
 * The numeric month-and-year format for a short date pattern: the day and one separator next to it
 * are removed (`DD/MM/YYYY` and `MM/DD/YYYY` → `MM/YYYY`, `YYYY-MM-DD` → `YYYY-MM`).
 */
export function monthYearFormat(shortDateFormat: string): string {
    return shortDateFormat.replace(/D{1,2}[^A-Za-z]|[^A-Za-z]D{1,2}/, '');
}

// The letters a format hint uses for the year, month and day, by the locale's language: Swedish
// writes ÅÅÅÅ-MM-DD (år) and Spanish DD/MM/AAAA (año). Other languages keep the English letters.
const HINT_LETTERS: Record<string, { Y: string; M: string; D: string }> = {
    sv: { Y: 'Å', M: 'M', D: 'D' },
    es: { Y: 'A', M: 'M', D: 'D' }
};

/**
 * A numeric date format written for people, as an input's placeholder or hint, in the letters the
 * locale's language uses: `YYYY-MM-DD` → `ÅÅÅÅ-MM-DD` (sv), `MM/DD/YYYY` → `MM/DD/AAAA` (es-US).
 * `locale` is the dayjs locale name (`sv`, `es`, `es-us`, `en-nz`…).
 */
export function formatHint(format: string, locale: string): string {
    const letters = HINT_LETTERS[locale.toLowerCase().split('-')[0]];
    return letters ? format.replace(/[YMD]/g, (c) => letters[c as 'Y' | 'M' | 'D']) : format;
}

/**
 * Named display formats (LIQ-2056). Show dates to users through these, by name, instead of format
 * strings: each one follows the active locale. Data (API parameters, map keys, bucketing) keeps
 * literal ISO formats such as `YYYY-MM-DD`.
 *
 * Decided for LIQ-2055: `date` is the locale's numeric short date, and times follow the locale's
 * clock (12-hour with am/pm for en-US and en-NZ).
 */
export type DatePreset =
    | 'date'        // 03/04/2026 (en-US) · 04/03/2026 (en-NZ) · 2026-03-04 (sv)
    | 'dateTime'    // 03/04/2026 2:05 PM · 04/03/2026 2:05 PM
    | 'time'        // 2:05 PM · 14:05 (sv)
    | 'dateMedium'  // Mar 4, 2026 (en-US) · 4 Mar 2026 (en-NZ) · 4 mar 2026 (sv)
    | 'dateLong'    // March 4, 2026 · 4 March 2026
    | 'dateTimeMedium' // Mar 4, 2026 2:05 PM · 4 Mar 2026 2:05 PM · 4 mar 2026 14:05 (sv)
    | 'monthYear'   // Mar 2026
    | 'monthShort'  // Mar
    | 'monthName'   // March
    | 'monthLong'   // March 2026
    | 'dayMonth'    // Mar 4 (en-US) · 4 Mar (en-NZ)
    | 'weekday'     // Wednesday
    | 'full'        // Wednesday, March 4, 2026 · Wednesday, 4 March 2026
    | 'tableDate'   // 2026-03-04 for everyone: date columns in tables and grids (D32, D40)
    | 'tableDateTime'; // 2026-03-04 14:05:00 for everyone: date-and-time columns in tables and grids

const PRESET_FORMATS: Record<Exclude<DatePreset, 'dayMonth'>, string> = {
    date: 'L',
    dateTime: 'L LT',
    time: 'LT',
    dateMedium: 'll',
    dateLong: 'LL',
    dateTimeMedium: 'lll',
    monthYear: 'MMM YYYY',
    monthShort: 'MMM',
    monthName: 'MMMM',
    monthLong: 'MMMM YYYY',
    weekday: 'dddd',
    full: 'dddd, LL',
    // Tables keep ISO dates for every locale: they sort well and can't be misread (decided 2026-11-02,
    // D32/D40). The date-time form matches the grids' existing `YYYY-MM-DD HH:mm:ss` columns.
    tableDate: 'YYYY-MM-DD',
    tableDateTime: 'YYYY-MM-DD HH:mm:ss'
};

/**
 * The day-and-short-month format in the locale's order, from its medium date pattern (dayjs `ll`):
 * the year and the separator before it are removed (`MMM D, YYYY` → `MMM D`, `D MMM YYYY` → `D MMM`,
 * `D [de] MMM [de] YYYY` → `D [de] MMM`).
 */
export function dayMonthFormat(mediumDateFormat: string): string {
    return mediumDateFormat.replace(/[\s,]*(\[de\]\s*)?Y{2,4}/, '').trim();
}

/**
 * The dayjs format string for a preset. `mediumDateFormat` is the active locale's `ll` pattern
 * (`dayjs().localeData().longDateFormat('ll')`), needed for `dayMonth` only.
 */
export function presetFormat(preset: DatePreset, mediumDateFormat: string): string {
    return preset === 'dayMonth' ? dayMonthFormat(mediumDateFormat) : PRESET_FORMATS[preset];
}
